/**
 * **같은 방덱의 학습 기록 중 무엇을 보이나 — 사이트 쪽** (사용자 2026-11-05 "최신 기록만 보이지 말고 합격 덱이 있는 기록·수동 학습 기록을 앞세운다").
 *
 * 순서의 뜻은 엔진 한곳에서 정한다(swSim engine/src/memoryview.ts — 기록마다 tier 를 실어 준다). 여기서는 tier·at 만 비교한다.
 * 엔진은 몹 번호로만 묶어 콜라보 짝을 모르므로, 콜라보 짝까지 묶는 일(학습 기록 창·전적검색)만 여기서 한 번 더 한다.
 * tier 가 없는 기록(방덱 학습 기록)끼리는 전처럼 최신이다.
 */
import type { MemoryRecord } from '../Page/SimMemory';

/** 보이지 않는 기록의 요약 (swSim engine/src/memoryview.ts RecordSummary) */
export interface RecordSummary {
    at: string;
    defense: number[];
    source: MemoryRecord['source'];
    tier?: number;
    passed?: number | null;
    decks: number;
}

export const summaryOf = (r: MemoryRecord): RecordSummary => ({ at: r.at, defense: r.defense, source: r.source, tier: r.tier, passed: r.passed, decks: r.decks?.length ?? 0 });

/** 앞선 기록 — 등급(tier 1 이 앞. 없는 기록끼리는 같은 등급), 그다음 최신. 시각까지 같으면 뒤에 받은 것 */
function bestOf(records: MemoryRecord[]): MemoryRecord | null {
    const tierOf = (r: MemoryRecord) => r.tier ?? 0;
    return records.reduce<MemoryRecord | null>((m, r) => (!m || tierOf(r) < tierOf(m) || (tierOf(r) === tierOf(m) && r.at >= m.at) ? r : m), null);
}

/** 앞선 기록에 나머지(알릴 만한 것)와 그 안에 든 요약을 others 로 합친다 */
function withOthers(best: MemoryRecord, records: MemoryRecord[]): MemoryRecord {
    if (records.length === 1) return best;
    const others: RecordSummary[] = [];
    for (const r of records) {
        if (r !== best && r.listed) others.push(summaryOf(r));
        others.push(...(r.others || []));
    }
    others.sort((a, b) => b.at.localeCompare(a.at));
    return others.length ? { ...best, others } : best;
}

/** 같은 방덱 기록 중 앞선 하나 (전적검색 — 받은 기록이 모두 한 방덱) */
export function pickRecord(records: MemoryRecord[]): MemoryRecord | null {
    const best = bestOf(records);
    return best && withOthers(best, records);
}

/** 같은 방덱(keyOf)마다 하나만 — 차례는 받은 그대로 */
export function bestPerDefense(keyOf: (defense: number[]) => string, records: MemoryRecord[]): MemoryRecord[] {
    const groups = new Map<string, MemoryRecord[]>();
    records.forEach((r) => {
        const k = keyOf(r.defense);
        const g = groups.get(k);
        if (g) g.push(r); else groups.set(k, [r]);
    });
    const shown = new Map<MemoryRecord, MemoryRecord>();
    groups.forEach((g) => {
        const best = bestOf(g)!;
        shown.set(best, withOthers(best, g));
    });
    return records.filter((r) => shown.has(r)).map((r) => shown.get(r)!);
}

/** 방금 끝난 [공덱 학습]의 기록 — 그 방덱 기록(최신 먼저)에서 가장 최근 수동 학습. 수동 표시 전에 올린 기록이면 가장 최근 것 */
export const justTrainedRecord = (newestFirst: MemoryRecord[]): MemoryRecord | undefined => newestFirst.find((r) => r.source === 'site') ?? newestFirst[0];

/** 이 방덱(리더는 자리까지, 2·3번은 순서 없이)의 기록 전부, 최신 먼저 — 고른 방덱 하나라 전체 목록을 받지 않는다 */
export async function fetchDefenseRecords(defense: number[]): Promise<MemoryRecord[]> {
    const d = await fetch(`/api/sim/memory?slots=${encodeURIComponent(defense.join('|'))}&all=1`, { credentials: 'include' }).then((r) => r.json());
    if (!d?.success) throw new Error(d?.error || '학습 기록을 받지 못했습니다');
    return d.records || [];
}
